import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, MapPin, Phone, MessageCircle, Mail, Loader2, AlertCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", msg: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.email.trim() || !form.msg.trim()) return;
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.msg.trim(),
      });
      setSent(true);
      setForm({ name: "", email: "", msg: "" });
      setTimeout(() => setSent(false), 6000);
    } catch (ex) {
      setErr(apiErr(ex.response?.data?.detail) || "संदेश भेजने में समस्या हुई — कृपया दुबारा कोशिश करें।");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#EFEEE8] border-t border-[#0F0F0F]/20"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="mb-10 md:mb-14">
          <div className="eyebrow text-[#D92525] mb-3">संपर्क करें</div>
          <h2 className="font-serif-dev font-black text-[36px] md:text-[54px] leading-[1.02]">
            Contact <span className="italic">Us</span>
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left: form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-7 border border-[#0F0F0F] bg-white p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <span className="font-sans-dev text-[10px] tracking-[0.24em] uppercase text-[#4A4A4A]">नाम</span>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b border-[#0F0F0F] outline-none py-2 font-serif-dev text-[18px] placeholder:text-[#0F0F0F]/30"
                  placeholder="आपका नाम"
                  required
                />
              </label>
              <label className="block">
                <span className="font-sans-dev text-[10px] tracking-[0.24em] uppercase text-[#4A4A4A]">ईमेल आईडी</span>
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full bg-transparent border-b border-[#0F0F0F] outline-none py-2 font-serif-dev text-[18px] placeholder:text-[#0F0F0F]/30"
                  placeholder="you@example.com"
                  required
                />
              </label>
            </div>
            <label className="block mt-6">
              <span className="font-sans-dev text-[10px] tracking-[0.24em] uppercase text-[#4A4A4A]">आपका संदेश</span>
              <textarea
                data-testid="contact-message"
                value={form.msg}
                onChange={(e) => setForm({ ...form, msg: e.target.value })}
                rows={6}
                className="mt-1 w-full bg-transparent border-b border-[#0F0F0F] outline-none py-2 font-serif-dev text-[17px] placeholder:text-[#0F0F0F]/30 resize-none"
                placeholder="यहाँ लिखें..."
                required
              />
            </label>
            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="font-sans-dev text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A]">
                हम जल्द जवाब देंगे
              </p>
              <button data-testid="contact-submit" type="submit" disabled={busy} className="pill-btn text-[12px] disabled:opacity-60">
                {busy ? <><Loader2 size={14} className="animate-spin" /> भेजा जा रहा है</> : <>भेजें <Send size={14} /></>}
              </button>
            </div>
            {err && (
              <div data-testid="contact-error" className="mt-5 flex items-start gap-2 text-[13px] text-[#D92525] bg-red-50 border border-red-200 rounded px-3 py-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> {err}
              </div>
            )}
            {sent && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="mt-6 flex items-start gap-2 text-[13px] text-[#0A0A0A] bg-emerald-50 border border-emerald-200 rounded px-3 py-2.5"
              >
                <CheckCircle2 size={16} className="text-[#D92525] mt-0.5 shrink-0" />
                <span>धन्यवाद! आपका संदेश हमारी संपादकीय टीम तक पहुँच गया है। पुष्टिकरण मेल आपके inbox पर भेज दिया गया है।</span>
              </motion.div>
            )}
          </motion.form>

          {/* Right: address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="contact-address-block"
            className="col-span-12 lg:col-span-5 bg-[#0A0A0A] text-[#F4F4F0] p-8 md:p-10"
          >
            <div className="eyebrow text-[#D92525] mb-4">Corporate Office</div>
            <h3 className="font-serif-dev font-black text-[26px] md:text-[30px] mb-6">Denvapost.com</h3>

            <ul className="space-y-6 font-sans-dev text-[15px] leading-relaxed">
              <li className="flex gap-3">
                <MapPin size={18} className="text-[#D92525] shrink-0 mt-0.5" />
                <span>78, Sirwar Road, Silari Colony, Makhan Nagar, MP-461661</span>
              </li>
              <li className="flex gap-3">
                <Phone size={18} className="text-[#D92525] shrink-0 mt-0.5" />
                <span>Office: <a href="tel:07574298163" className="link-underline">07574-298163</a></span>
              </li>
              <li className="flex gap-3">
                <MessageCircle size={18} className="text-[#D92525] shrink-0 mt-0.5" />
                <span>WhatsApp: <a href="https://wa.me/919981814651" className="link-underline">+91 99818 14651</a></span>
              </li>
            </ul>

            <div className="mt-8 pt-6 border-t border-white/15 space-y-3 font-sans-dev text-[14px]">
              <div className="flex gap-3">
                <Mail size={16} className="text-[#D92525] shrink-0 mt-0.5" />
                <span>विज्ञापन हेतु: <a href="mailto:sales@denvapost.com" className="link-underline">sales@denvapost.com</a></span>
              </div>
              <div className="flex gap-3">
                <Mail size={16} className="text-[#D92525] shrink-0 mt-0.5" />
                <span>अन्य प्रश्न: <a href="mailto:feedback@denvapost.com" className="link-underline">feedback@denvapost.com</a></span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
